import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box, Button, Paper, TextField, Typography, Alert, Stack, Link,
  InputAdornment, CircularProgress,
} from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LockResetIcon from "@mui/icons-material/LockReset";
import { forgotPassword } from "../data/api";
import { BRAND } from "../theme";

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await forgotPassword(email.trim());
    } catch {
      /* generic by design — never reveal whether the email exists */
    } finally {
      setSent(true);
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", p: 2,
      background: `linear-gradient(135deg, ${BRAND.greenDark} 0%, ${BRAND.green} 100%)` }}>
      <Paper elevation={0} sx={{ width: "100%", maxWidth: 420, p: { xs: 3, sm: 4 }, borderRadius: 3 }}>
        <Stack spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
          <Box sx={{ bgcolor: BRAND.greenSoft, color: BRAND.green, width: 56, height: 56, borderRadius: 2, display: "grid", placeItems: "center" }}>
            <LockResetIcon fontSize="large" />
          </Box>
          <Typography variant="h5">Reset your password</Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            Enter your account email and we'll send a reset link.
          </Typography>
        </Stack>

        {sent ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            If an account exists for that email, a password reset link has been sent.
            The link expires shortly and can be used once.
          </Alert>
        ) : (
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth label="Email" type="email" margin="normal" value={email} autoFocus required
              onChange={(e) => setEmail(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><MailOutlineIcon fontSize="small" /></InputAdornment> }}
            />
            <Button type="submit" fullWidth variant="contained" size="large" disabled={loading} sx={{ mt: 2.5, py: 1.2 }}>
              {loading ? <CircularProgress size={22} color="inherit" /> : "Send reset link"}
            </Button>
          </Box>
        )}

        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
          <Link component={RouterLink} to="/login" underline="hover" sx={{ fontWeight: 600 }}>Back to sign in</Link>
        </Typography>
      </Paper>
    </Box>
  );
};

export default ForgotPasswordPage;
